import type { TypedSupabaseClient } from '../client';
import type {
  IPaymentRepository,
  Payment,
  PaymentAllocation,
  PaymentFilters,
  PaymentType,
  CreatePaymentInput,
} from '../../../interfaces/src';

const PAYMENT_TYPE_PREFIX: Record<PaymentType, string> = {
  receipt: 'ENC',
  disbursement: 'DEC',
};

const PAYMENT_TYPE_SEQUENCE: Record<PaymentType, string> = {
  receipt: 'seq_payment_receipt',
  disbursement: 'seq_payment_disbursement',
};

export class SupabasePaymentRepository implements IPaymentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: PaymentFilters): Promise<Payment[]> {
    if (filters?.documentId) {
      const { data: allocs, error: allocErr } = await this.supabase
        .from('payment_allocations')
        .select('payment_id')
        .eq('document_id', filters.documentId)
        .eq('document_type', filters.documentType ?? 'delivery');
      if (allocErr) throw allocErr;
      const paymentIds = (allocs ?? []).map((a: { payment_id: string }) => a.payment_id);
      if (paymentIds.length === 0) return [];

      const { data, error } = await this.supabase
        .from('payments')
        .select('*, payment_allocations(*)')
        .in('id', paymentIds)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => mapPayment(row));
    }

    let query = this.supabase.from('payments').select('*, payment_allocations(*)');

    if (filters?.paymentType) query = query.eq('payment_type', filters.paymentType);
    if (filters?.thirdPartyType) query = query.eq('third_party_type', filters.thirdPartyType);
    if (filters?.thirdPartyId) query = query.eq('third_party_id', filters.thirdPartyId);
    if (filters?.dateFrom) query = query.gte('payment_date', filters.dateFrom);
    if (filters?.dateTo) query = query.lte('payment_date', filters.dateTo);
    if (filters?.recurringPaymentId)
      query = query.eq('recurring_payment_id', filters.recurringPaymentId);

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((row) => mapPayment(row));
  }

  async getById(id: string): Promise<Payment | null> {
    const { data, error } = await this.supabase
      .from('payments')
      .select('*, payment_allocations(*)')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapPayment(data);
  }

  async create(input: CreatePaymentInput): Promise<Payment> {
    const number = await this.getNextNumber(input.paymentType);

    let cashSessionId: string | null = null;
    if (input.paymentMethod === 'cash') {
      // Pas bloquant : si aucune caisse ouverte, on enregistre le paiement
      // sans lien de session (pas de cash_movement créé). La caisse n'est
      // obligatoire que quand on l'utilise réellement (ouverture/clôture).
      cashSessionId = await this.resolveOpenCashSession(input.bankAccountId);
    }

    const insertPayload: Record<string, unknown> = {
      number,
      payment_type: input.paymentType,
      payment_method: input.paymentMethod,
      third_party_type: input.thirdPartyType,
      third_party_id: input.thirdPartyId,
      bank_account_id: input.bankAccountId,
      amount: input.amount,
      payment_date: input.paymentDate,
      reference: input.reference,
      notes: input.notes,
    };
    if (cashSessionId) insertPayload['cash_session_id'] = cashSessionId;
    if (input.recurringPaymentId) insertPayload['recurring_payment_id'] = input.recurringPaymentId;

    const { data: payment, error: paymentError } = await this.supabase
      .from('payments')
      .insert(insertPayload as never)
      .select()
      .single();
    if (paymentError) throw paymentError;

    if (input.allocations && input.allocations.length > 0) {
      const allocations = input.allocations.map((alloc) => ({
        payment_id: payment.id,
        document_type: alloc.documentType,
        document_id: alloc.documentId,
        allocated_amount: alloc.allocatedAmount,
      }));

      const { error: allocError } = await this.supabase
        .from('payment_allocations')
        .insert(allocations);
      if (allocError) {
        await this.supabase.from('payments').delete().eq('id', payment.id);
        throw allocError;
      }
    }

    if (cashSessionId) {
      const movementType =
        input.paymentType === 'receipt' ? 'sale_receipt' : 'payment_receipt';
      const mvtNumber = await this.nextCashMovementNumber();
      const { error: mvtError } = await this.supabase
        .from('cash_movements')
        .insert({
          number: mvtNumber,
          session_id: cashSessionId,
          movement_type: movementType,
          amount: input.amount,
          description: `Paiement ${number}`,
          payment_id: payment.id,
        } as never);
      if (mvtError) {
        // Compensation: clear the session link to keep reconciliation accurate
        await this.supabase
          .from('payments')
          .update({ cash_session_id: null } as never)
          .eq('id', payment.id);
        throw mvtError;
      }
    }

    return (await this.getById(payment.id))!;
  }

  /**
   * Retourne l'id d'une caisse ouverte à lier au paiement, ou null si aucune.
   *
   * Un paiement cash peut être saisi sans caisse ouverte : on conserve
   * simplement le paiement sans lien de session (pas de mouvement de caisse
   * créé). La caisse n'est obligatoire que pour l'ouverture/clôture.
   * Plusieurs sessions ouvertes → on ne choisit pas (null aussi), pour
   * éviter un mouvement dans la mauvaise caisse.
   */
  private async resolveOpenCashSession(bankAccountId?: string): Promise<string | null> {
    if (bankAccountId) {
      const { data, error } = await this.supabase
        .from('cash_sessions')
        .select('id')
        .eq('bank_account_id', bankAccountId)
        .eq('is_closed', false)
        .maybeSingle();
      if (error) throw error;
      return data ? (data as { id: string }).id : null;
    }

    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select('id')
      .eq('is_closed', false);
    if (error) throw error;
    const sessions = data ?? [];
    if (sessions.length !== 1) return null;
    return (sessions[0] as { id: string }).id;
  }

  private async nextCashMovementNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const { data } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: 'seq_cash_movement',
    });
    if (data != null) return `MVT-${year}-${String(data).padStart(4, '0')}`;
    const { count } = await this.supabase
      .from('cash_movements')
      .select('*', { count: 'exact', head: true });
    return `MVT-${year}-${String((count ?? 0) + 1).padStart(4, '0')}`;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('payments').delete().eq('id', id);
    if (error) throw error;
  }

  async getNextNumber(type: PaymentType): Promise<string> {
    const prefix = PAYMENT_TYPE_PREFIX[type];
    const seqName = PAYMENT_TYPE_SEQUENCE[type];
    const year = new Date().getFullYear();

    if (seqName) {
      const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
        seq_name: seqName,
      });
      if (!error && data != null) {
        return `${prefix}-${year}-${String(data).padStart(4, '0')}`;
      }
    }

    // Fallback: count existing payments + 1
    const { count } = await this.supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .eq('payment_type', type);
    const next = (count ?? 0) + 1;
    return `${prefix}-${year}-${String(next).padStart(4, '0')}`;
  }
}

function mapPayment(row: Record<string, unknown>): Payment {
  const allocations =
    (row['payment_allocations'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    paymentType: row['payment_type'] as Payment['paymentType'],
    paymentMethod: row['payment_method'] as Payment['paymentMethod'],
    thirdPartyType: row['third_party_type'] as Payment['thirdPartyType'],
    thirdPartyId: row['third_party_id'] as string,
    bankAccountId: (row['bank_account_id'] as string) ?? null,
    cashSessionId: (row['cash_session_id'] as string) ?? null,
    amount: row['amount'] as number,
    paymentDate: row['payment_date'] as string,
    reference: (row['reference'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    recurringPaymentId: (row['recurring_payment_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    allocations: allocations.map(mapPaymentAllocation),
  };
}

function mapPaymentAllocation(row: Record<string, unknown>): PaymentAllocation {
  return {
    id: row['id'] as string,
    paymentId: row['payment_id'] as string,
    documentType: row['document_type'] as string,
    documentId: row['document_id'] as string,
    allocatedAmount: row['allocated_amount'] as number,
  };
}
